import { Divider, Grid } from '@mui/material';
import { RoutalLoader, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RoutalLogoGreen from '../../../../../assets/img/routal-star-green.svg';
import { DividerWithText } from '../../../../../components/DividerWithText';
import { LoadingButton } from '../../../../../components/LoadingButton';
import { Modal } from '../../../../../components/Modal';
import * as routing from '../../../../../constants/Routing';
import { STOP_STATUS } from '../../../../../constants/Stops';
import { Route } from '../../../../../domain/route/Route';
import { Stop } from '../../../../../domain/stops/Stop';
import { intl } from '../../../../../helpers/IntGlobalProvider';
import { useInterval } from '../../../../../hooks/useInterval';
import { Text } from '../../../../../react-components/Text';
import { usePermissions } from '../../../hooks/usePermissions';
import { useUpdateRouteStopsOrder } from '../../hooks/useUpdateRouteStopsOrder';
import { CreateAndReorderStopsMap, ReorderMapStops } from '../CreateAndReorderStopsMap';
import { useRetrieveStaticHeight } from '../hooks/useRetrieveStaticHeight';
import { ReorderStopPill } from './ReorderStopPill';

type ReorderRouteProps = {
  selectedRoute: Route;
};

export const ReorderRoute = ({ selectedRoute }: ReorderRouteProps) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { canReorderStops } = usePermissions();
  const { mapHeight, contentHeight } = useRetrieveStaticHeight();
  const [selectedStops, setSelectedStops] = useState<string[]>([]);
  const [lastOptimization, setLastOptimization] = useState<boolean>(false);
  const {
    run: reorderRouteStops,
    isLoading,
    success,
    reset,
    error,
  } = useUpdateRouteStopsOrder({ driverRoute: selectedRoute });
  const [dialog, setDialog] = useState<
    `reoptimize` | `reorder` | `loading` | `optimization_error` | `optimization_conflicts` | null
  >(null);
  const params = new URLSearchParams(search);
  const stopId = params.get(`stop`);

  useEffect(() => {
    if (!canReorderStops) navigate(routing.PRIVATE_ROUTES.ROUTE_DETAILS.replace(`:routeId`, selectedRoute.id));
    if (stopId && selectedRoute.stops.find((stop) => stop.id === stopId)?.status === `pending`)
      setSelectedStops([stopId]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //Interval in case the optimizer took too long
  const { reset: resetinterval } = useInterval({
    callback: () => {
      if (isLoading) setDialog(`optimization_error`);
    },
    enabled: isLoading,
    delay: 60000,
  });

  // Handle click event
  const handleOnClick = (stopId: string) => {
    if (selectedStops.includes(stopId)) {
      // If stop is already selected, remove it
      const possible_pickup = selectedRoute.stops.find((stop) => stop.pickup_id === stopId);

      if (possible_pickup) {
        setSelectedStops(selectedStops.filter((id) => id !== possible_pickup.id && id !== stopId));
      } else {
        setSelectedStops(selectedStops.filter((id) => id !== stopId));
      }
    } else {
      // If stop is not selected, add it
      setSelectedStops([...selectedStops, stopId]);
    }
  };

  useEffect(() => {
    if (!isLoading && success) {
      const hasAnyOptimizationWarning = selectedRoute?.stops.some((stop) => stop.optimization_warning);
      resetinterval();
      if (hasAnyOptimizationWarning) setDialog(`optimization_conflicts`);
      else {
        setSelectedStops([]);
        reset();
        setDialog(null);
        navigate(routing.PRIVATE_ROUTES.ROUTE_DETAILS.replace(`:routeId`, selectedRoute.id));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  useEffect(() => {
    if (error) {
      setDialog(`optimization_error`);
    }
  }, [error]);

  useEffect(() => {
    if (isLoading) {
      setDialog(`loading`);
    }
  }, [isLoading]);

  const pendingNotSelectedStopsList = selectedRoute.stops.filter(
    (stop) => stop.status === STOP_STATUS.pending && !selectedStops.includes(stop.id)
  );
  const pendingSelectedStopsList = selectedStops
    .map(
      (stopId) => selectedRoute.stops.find((stop) => stop.id === stopId && stop.status === STOP_STATUS.pending) || null
    )
    .filter(Boolean) as Stop[];

  const reorderedStops = [...pendingSelectedStopsList, ...pendingNotSelectedStopsList].map((stop, index) => {
    return stop;
  });

  const reorderMapStops = {
    selectedStops: selectedStops,
    reorderedStops: reorderedStops,
  } as ReorderMapStops;

  return (
    <>
      <Modal
        open={dialog === `optimization_error` || dialog === `optimization_conflicts`}
        title={{
          intlId: `driver.stops.reorder_route.${dialog}_dialog.title`,
        }}
        subtitle={{
          intlId: `driver.stops.reorder_route.${dialog}_dialog.subtitle`,
        }}
        buttons={
          dialog === `optimization_error`
            ? [
                {
                  intlId: `driver.stops.reorder_route.optimization_error_dialog.retry_button`,
                  onClick: () => {
                    reorderRouteStops({ stops: selectedStops, optimize: lastOptimization });
                  },
                  disabled: isLoading || !error,
                },
                {
                  intlId: `cancel`,
                  onClick: () => {
                    setDialog(null);
                  },
                  disabled: isLoading || !error,
                  variant: `text`,
                  style: {
                    border: `none`,
                  },
                },
              ]
            : [
                {
                  intlId: `accept`,
                  onClick: () => {
                    setDialog(null);
                    setSelectedStops([]);
                    reset();
                    navigate(routing.PRIVATE_ROUTES.ROUTE_DETAILS.replace(`:routeId`, selectedRoute.id));
                  },
                },
              ]
        }
        children={<VSpacer medium />}
      />
      <Modal
        open={dialog === `reoptimize` || dialog === `reorder`}
        title={{
          intlId: `driver.stops.reorder_route.modal.${dialog}.title`,
        }}
        subtitle={{
          intlId: `driver.stops.reorder_route.modal.${dialog}.info`,
          intlValues: {
            pendingServices: selectedRoute.stops.filter((s) => s.status === STOP_STATUS.pending).length,
          },
        }}
        buttons={[
          ...(dialog === `reorder`
            ? [
                {
                  disabled: isLoading,
                  intlId: `driver.stops.reorder_route.modal.reorder.reoptimize`,
                  onClick: () => {
                    reorderRouteStops({ stops: selectedStops, optimize: true });
                    setLastOptimization(true);
                  },
                },
                {
                  disabled: isLoading,
                  variant: `flat`,
                  intlId: `driver.stops.reorder_route.modal.reorder.reorder`,
                  onClick: () => {
                    reorderRouteStops({ stops: selectedStops });
                    setLastOptimization(false);
                  },
                },
              ]
            : [
                {
                  disabled: isLoading,
                  intlId: `accept`,
                  onClick: () => {
                    reorderRouteStops({ stops: [], optimize: true });
                    setLastOptimization(true);
                  },
                },
              ]),
          {
            intlId: `cancel`,
            onClick: () => {
              setDialog(null);
            },
            variant: `text`,
            style: { marginBottom: `0px` },
          },
        ]}
        children={<VSpacer medium />}
      />
      <Modal
        open={dialog === `loading`}
        children={
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
            wrap="nowrap"
            style={{ marginTop: `50px`, marginBottom: `60px` }}
          >
            <div style={{ position: `relative` }}>
              <RoutalLoader width={60} strokeWidth={1} />

              <img
                style={{ width: `34px`, top: `28px`, left: `30px`, position: `absolute` }}
                src={RoutalLogoGreen}
                alt=""
                draggable={false}
              />
            </div>

            <Text intlId="driver.stops.reorder_route.loading_optimization" variant="h6-light" />
          </Grid>
        }
      />
      <Grid
        style={{
          position: `absolute`,
          top: `0px`,
          left: `0px`,
          zIndex: 1003,
          width: `100%`,
          pointerEvents: `none`,
        }}
      >
        <CreateAndReorderStopsMap
          selectedRoute={selectedRoute}
          mapHeight={mapHeight}
          reorderMapStops={reorderMapStops}
          onMarkerClick={(stop: Stop) => {
            if (stop.status === STOP_STATUS.pending) {
              if (selectedStops.includes(stop.id)) {
                // If stop is already selected, remove it
                setSelectedStops(selectedStops.filter((id) => id !== stop.id));
              } else {
                // If stop is not selected, add it
                setSelectedStops([...selectedStops, stop.id]);
              }
            }
          }}
        />
      </Grid>
      <Grid
        container
        direction="column"
        style={{
          position: `absolute`,
          left: 0,
          right: 0,
          bottom: 0,
          height: `${contentHeight}px`,
          zIndex: 1000,
          backgroundColor: RoutalPalette.white,
        }}
      >
        <Grid
          style={{
            display: `flex`,
            flexDirection: `column`,
            alignItems: `center`,
            padding: `20px`,
            textAlign: `center`,
          }}
        >
          <Text variant="h6" intlId="driver.stops.reorder_route.title" />
          <VSpacer small />
          <Text variant="label" intlId="driver.stops.reorder_route.subtitle" />
        </Grid>

        <Divider style={{ width: `100%` }} />

        <Grid
          container
          direction="column"
          alignItems="center"
          style={{
            padding: `12px 0px`,
            overflowY: `auto`,
            flex: 1,
            flexWrap: `nowrap`,
          }}
        >
          {pendingSelectedStopsList.map((stop, index) => {
            if (!stop) return null;
            return (
              <Grid style={{ width: `100%` }}>
                <ReorderStopPill
                  stop={stop}
                  stopType={stop.type}
                  styleTag={{
                    backgroundColor: RoutalPalette.secondary40,
                    borderColor: RoutalPalette.secondary40,
                    color: RoutalPalette.white,
                  }}
                  tagStyle={`highlighted`}
                  handleOnClick={function (stop_id: string): void {
                    handleOnClick(stop_id);
                  }}
                  order={index + 1}
                  containerStyles={{ borderColor: RoutalPalette.primary40, padding: `0px 20px` }}
                />
                <VSpacer small />
              </Grid>
            );
          })}

          {selectedStops.length > 0 && (
            <Grid
              style={{
                width: `100%`,
                display: `flex`,
                alignItems: `center`,
                flexDirection: `column`,
                padding: `0px 20px`,
              }}
            >
              <VSpacer medium />

              <DividerWithText text={intl.formatMessage({ id: `driver.stops.reorder_route.pending_stops` })} />

              <VSpacer medium />
            </Grid>
          )}

          {pendingNotSelectedStopsList.map((stop, index) => {
            // let selected = false;
            // if (selectedStops.includes(stop.id)) {
            //   selected = true;
            // }
            // const order = selected ? stop.order : selectedStops.indexOf(stop.id) + 1;
            const order = selectedStops.length + 1 + index;
            return (
              <Grid style={{ width: `100%` }}>
                <ReorderStopPill
                  stop={stop}
                  stopType={stop.type}
                  styleTag={{
                    backgroundColor: RoutalPalette.primary00,
                    borderColor: RoutalPalette.primary40,
                    color: RoutalPalette.primary40,
                  }}
                  containerStyles={{
                    // backgroundColor: selected ? RoutalPalette.neutral05 : `transparent`,
                    backgroundColor: `transparent`,
                    padding: `0px 20px`,
                    opacity:
                      stop.type === `delivery` &&
                      stop.pickup_id &&
                      !selectedStops.includes(stop.pickup_id) &&
                      selectedRoute.stops.find((s) => s.id === stop.pickup_id)?.status === STOP_STATUS.pending
                        ? 0.5
                        : 1,
                  }}
                  tagStyle={`highlighted`}
                  handleOnClick={function (stop_id: string): void {
                    if (
                      stop.type === `delivery` &&
                      stop.pickup_id &&
                      !selectedStops.includes(stop.pickup_id) &&
                      selectedRoute.stops.find((s) => s.id === stop.pickup_id)?.status === STOP_STATUS.pending
                    )
                      return;

                    handleOnClick(stop_id);
                  }}
                  order={order}
                />
                <VSpacer small />
              </Grid>
            );
          })}
        </Grid>

        <Grid
          container
          direction="row"
          justifyContent="space-evenly"
          alignContent="center"
          flexWrap="nowrap"
          alignItems="center"
          style={{
            padding: `12px 20px`,
            height: `70px`,
            borderTop: `1px solid ${RoutalPalette.white}`,
            width: `100%`,
            background: RoutalPalette.white,
          }}
        >
          <LoadingButton
            //   isLoading={isLoading}
            style={{
              height: `42px`,
              backgroundColor: RoutalPalette.white,
              border: `1px solid ${RoutalPalette.primary40}`,
              display: `flex`,
              flexDirection: `row`,
              flexWrap: `nowrap`,
              justifyContent: `center`,
              alignItems: `center`,
              borderRadius: `10px`,
              width: `100%`,
              marginRight: `10px`,
            }}
            onClick={(e: any) => {
              navigate(routing.PRIVATE_ROUTES.ROUTE_DETAILS.replace(`:routeId`, selectedRoute.id));
            }}
          >
            <Text intlId={`cancel`} style={{ color: RoutalPalette.primary40 }} />
          </LoadingButton>

          <LoadingButton
            //   isLoading={isLoading}
            style={{
              height: `42px`,
              backgroundColor: selectedStops.length > 0 ? RoutalPalette.secondary40 : RoutalPalette.primary40,
              borderColor: selectedStops.length > 0 ? RoutalPalette.secondary40 : RoutalPalette.primary40,
              border: `1px solid ${selectedStops.length > 0 ? RoutalPalette.secondary40 : RoutalPalette.primary40}`,
              display: `flex`,
              flexDirection: `row`,
              flexWrap: `nowrap`,
              justifyContent: `center`,
              alignItems: `center`,
              borderRadius: `10px`,
              width: `100%`,
            }}
            onClick={(e: any) => {
              if (selectedStops.length === 0) {
                setDialog(`reoptimize`);
              } else {
                setDialog(`reorder`);
              }
            }}
          >
            {selectedStops.length === 0 ? (
              <Text intlId={`driver.stops.reorder_route.reoptimize`} style={{ color: RoutalPalette.white }} />
            ) : (
              <Text intlId={`driver.stops.reorder_route.reorder`} style={{ color: RoutalPalette.white }} />
            )}
          </LoadingButton>
        </Grid>
      </Grid>
    </>
  );
};
